<script lang="ts">
  /**
   * A person's round picture (TwinTen ADR 0073): the picture when there is
   * one, else their initial on an accent-tinted circle, else (a guest) the
   * generic person. One ladder of sizes, so a list, a seat, the profile
   * button and a profile card agree:
   *
   * | size | rem | where |
   * |---|---|---|
   * | `xs` | 1.5 | inside a line of text (an opponent strip, a suggestion) |
   * | `sm` | 2 | a lobby seat |
   * | `md` | 2.4 | a friend's row, the profile button |
   * | `lg` | 3.5 | a sheet's header |
   * | `xl` | 6 | a profile card, the profile editor |
   *
   * - `src`: the picture's URL. A picture that fails to load falls back to
   *   the initial, so a removed or unreachable picture never draws a broken
   *   image.
   * - `initial`: the letter; the name's first character by default. `null`
   *   draws the generic person (a guest).
   * - `tone` of the circle with no picture: `tint` (the default: 10% accent
   *   over the card, ink letter), `accent` (filled, the profile button's),
   *   `surface` (raised, accent letter: a friend's row).
   * - `label`: with one, the avatar is an image of that name for assistive
   *   technology; without, it is decoration (the name is beside it).
   *
   * Test id: `testid`, on the circle; `data-avatar` is `picture`,
   * `initial` or `person` — what it is drawing.
   */
  interface Props {
    /** Whose it is: the initial comes from it, and a picture's `alt` when `label` is absent. */
    readonly name: string;
    readonly src?: string | null | undefined;
    readonly initial?: string | null | undefined;
    readonly size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    readonly tone?: 'tint' | 'accent' | 'surface';
    readonly label?: string | undefined;
    readonly testid?: string | undefined;
  }
  const { name, src, initial, size = 'md', tone = 'tint', label, testid }: Props = $props();

  /** The URL that failed, so a new one is tried again. */
  let failed = $state<string | null>(null);

  const picture = $derived(
    src !== undefined && src !== null && src !== '' && src !== failed ? src : null,
  );
  const letter = $derived.by(() => {
    if (initial === null) return null;
    const chosen = initial ?? [...name.trim()][0] ?? '';
    return chosen.length > 0 ? chosen.toUpperCase() : null;
  });
  const drawing = $derived(picture !== null ? 'picture' : letter !== null ? 'initial' : 'person');
</script>

<span
  class="ui-avatar ui-avatar--{size} ui-avatar--{tone}"
  role={label === undefined ? undefined : 'img'}
  aria-label={label}
  aria-hidden={label === undefined ? 'true' : undefined}
  data-testid={testid}
  data-avatar={drawing}
>
  {#if picture !== null}
    <img
      class="ui-avatar-img"
      src={picture}
      alt=""
      decoding="async"
      draggable="false"
      onerror={() => (failed = picture)}
    />
  {:else if letter !== null}
    {letter}
  {:else}
    <svg class="ui-avatar-person" viewBox="0 0 24 24">
      <path
        d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM4.5 20c.6-3.8 3.7-6 7.5-6s6.9 2.2 7.5 6"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
      />
    </svg>
  {/if}
</span>

<style>
  .ui-avatar {
    flex: 0 0 auto;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border-radius: 50%;
    font-family: var(--ui-font);
    line-height: 1;
    user-select: none;
  }

  .ui-avatar--tint {
    background: color-mix(in srgb, var(--ui-accent) 10%, var(--ui-card));
    color: var(--ui-ink);
  }

  .ui-avatar--accent {
    background: var(--ui-accent);
    color: var(--ui-on-accent);
  }

  .ui-avatar--surface {
    background: var(--ui-surface-2);
    color: var(--ui-accent);
  }

  .ui-avatar--xs {
    width: 1.5rem;
    height: 1.5rem;
    font: var(--ui-text-caption);
    font-weight: 800;
  }

  .ui-avatar--sm {
    width: 2rem;
    height: 2rem;
    font: var(--ui-text-label);
    font-weight: 800;
  }

  .ui-avatar--md {
    width: 2.4rem;
    height: 2.4rem;
    font: var(--ui-text-heading);
  }

  .ui-avatar--lg {
    width: 3.5rem;
    height: 3.5rem;
    font: var(--ui-text-title);
  }

  .ui-avatar--xl {
    width: 6rem;
    height: 6rem;
    font: var(--ui-text-display);
  }

  .ui-avatar-img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .ui-avatar-person {
    width: 55%;
    height: 55%;
  }
</style>
